import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { hex2rgba } from '../../../../../../common/utils/hex2rgba';
import { frameRateAtom } from '../../../../../labelling-v2/atoms/file-atoms';
import { timeDisplayModeAtom } from '../../../../../labelling-v2/atoms/tool-atoms';
import { TimeDisplayMode } from '../../../../../types';
import { getFrameToTime } from '../../../../../utils/frame-time';
import { FRAME_WIDTH, HOVERED_COLUMN_Z_INDEX } from './constants';
import type { HoveredColumnRefT } from './types';
type PropsT = {
  onFrameChange: (frameIndex: number | null) => void;
  isDisabled: boolean;
  totalFrames: number;
};
const Container = styled('div', ({
  $theme
}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: `${FRAME_WIDTH}px`,
  bottom: 0,
  pointerEvents: 'none',
  backgroundColor: `rgba(${hex2rgba($theme.colors.borderAccentLight, 0.5).join(',')})`,
  opacity: 0,
  zIndex: HOVERED_COLUMN_Z_INDEX,
  transition: 'transform 0s, opacity 0.05s',
  '::before': {
    content: 'attr(data-label)',
    position: 'absolute',
    top: '-14px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: `0 ${$theme.sizing.scale0}`,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    backgroundColor: $theme.colors.backgroundTertiary,
    borderRadius: $theme.borders.radius200,
    ...$theme.borders.border500,
    ...$theme.typography.LabelXSmall
  }
}));
type TGetHoveredColumnLabelProps = {
  columnIndex: number;
  timeDisplayMode: TimeDisplayMode;
  totalFrames: number;
  frameRate: number | null | undefined;
};
const getHoveredColumnLabel = ({
  columnIndex,
  timeDisplayMode,
  totalFrames,
  frameRate
}: TGetHoveredColumnLabelProps) => {
  if (timeDisplayMode === TimeDisplayMode.FRAMES) {
    return `Frame ${columnIndex + 1}`;
  }
  return getFrameToTime({
    frame: columnIndex,
    maxFrames: totalFrames,
    frameRate
  });
};
export const _HoveredColumn = forwardRef<HoveredColumnRefT, PropsT>(({
  onFrameChange,
  isDisabled,
  totalFrames
}, ref) => {
  const hoveredColumnIndexRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRate = useAtomValue(frameRateAtom);
  const timelineDisplayMode = useAtomValue(timeDisplayModeAtom);
  const onFrameChangeRef = useRef(onFrameChange);
  // eslint-disable-next-line react-hooks/refs
  onFrameChangeRef.current = onFrameChange;
  useImperativeHandle(ref, () => ({
    show(columnIndex: number, scrollLeft: number) {
      if (!containerRef.current) {
        return;
      }
      if (isDisabled) {
        return;
      }
      if (columnIndex !== hoveredColumnIndexRef.current) {
        hoveredColumnIndexRef.current = columnIndex;
        const columnLabel = getHoveredColumnLabel({
          columnIndex,
          timeDisplayMode: timelineDisplayMode,
          totalFrames,
          frameRate
        });
        containerRef.current.dataset.label = columnLabel;
        onFrameChangeRef.current(columnIndex);
      }
      containerRef.current.style.transform = `translateX(${columnIndex * FRAME_WIDTH - scrollLeft}px)`;
      containerRef.current.style.opacity = '1';
    },
    hide() {
      if (!containerRef.current) {
        return;
      }
      hoveredColumnIndexRef.current = null;
      containerRef.current.style.opacity = '0';
      containerRef.current.dataset.label = '';
      onFrameChangeRef.current(null);
    }
  }), [onFrameChangeRef, hoveredColumnIndexRef, containerRef, isDisabled, totalFrames, frameRate, timelineDisplayMode]);
  return <Container ref={containerRef} />;
});
_HoveredColumn.displayName = 'HoveredColumn';
export const HoveredColumn = React.memo(_HoveredColumn);